import { useCallback, useMemo, useState } from "react"

import { format, setHours, setMinutes, startOfDay } from "date-fns"
import { toast } from "sonner"

import { useSavePlannedRoute } from "@/hooks/useCollections"
import { formatDayMonth } from "@/lib/dateFormats"
import { firstEditableMonday } from "@/lib/planLock"
import {
  cloneRouteDates,
  dayOfWeekOf,
  formatTimeForDb,
  futureExcludedDates,
  plannedStopsPayload,
  recurringDaysForSave,
  routeEqualsOriginal,
  shiftStopsToDate,
} from "@/lib/plannedRouteMapping"
import { resolveRouteKind } from "@/lib/routeCostCalculation"
import type { PlannedRoute, RouteCategory, RoutePlanned, VehicleType } from "@/types/collections"

import type { EditableRoute, InstanceEditMode, RouteFormData } from "./types"

/** Unlike stop edits, the route dialog persists as soon as it is saved. */
export function usePlanningRouteDialog({
  routesPlannedData,
  vehicleTypes,
  routes,
  setRoutes,
  originalRoutes,
  setOriginalRoutes,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  selectedDate,
  setInstanceEditMode,
  setIsSaving,
  refetchRoutes,
  refetchInstances,
}: {
  routesPlannedData: { data: RoutePlanned[] } | undefined
  vehicleTypes: VehicleType[] | undefined
  routes: PlannedRoute[]
  setRoutes: (routes: PlannedRoute[]) => void
  originalRoutes: PlannedRoute[]
  setOriginalRoutes: (routes: PlannedRoute[]) => void
  hasUnsavedChanges: boolean
  setHasUnsavedChanges: (value: boolean) => void
  selectedDate: Date
  setInstanceEditMode: (mode: InstanceEditMode) => void
  setIsSaving: (value: boolean) => void
  refetchRoutes: () => Promise<unknown>
  refetchInstances: () => Promise<unknown>
}) {
  const [editRouteDialogOpen, setEditRouteDialogOpen] = useState(false)
  const [selectedRouteForEdit, setSelectedRouteForEdit] = useState<PlannedRoute | null>(null)
  const savePlannedRoute = useSavePlannedRoute()

  const vehicleTypeOptions = useMemo(() => {
    if (!vehicleTypes) {
      return [
        "HGV-Curtainsider-350mi",
        "HGV-Box-350mi",
        "HGV-Curtainsider-300mi",
        "HGV-Box-280mi",
        "Luton Van-Box-120mi",
        "Panel Van-Box-130mi",
        "Small Van-Box-140mi",
      ]
    }
    return vehicleTypes
      .filter((vt) => vt.is_active)
      .map((vt) => vt.display_name || vt.name)
      .sort()
  }, [vehicleTypes])

  const [routeFormData, setRouteFormData] = useState<RouteFormData>({
    name: "",
    category: "Other",
    operator: "HIVED",
    vehicleType: "HGV-Curtainsider-350mi",
    crewSize: 1,
    routeKind: "operational",
    status: "draft",
    isRecurring: false,
    recurringDays: [],
    effectiveFromDate: undefined,
    effectiveToDate: undefined,
    excludedDates: [],
    startTime: "",
    endTime: "",
  })

  const [isCreatingNewRoute, setIsCreatingNewRoute] = useState(false)

  /** Compared to the saved code, so typing it back clears the rename warning. */
  const isRenamingRoute =
    !isCreatingNewRoute &&
    !!selectedRouteForEdit &&
    routeFormData.name.trim() !== "" &&
    routeFormData.name.trim() !== selectedRouteForEdit.code

  /** Defaults to the earliest Monday outside the locked week (signed off the Friday before); can only move later. */
  const [renameFromDate, setRenameFromDate] = useState<Date | undefined>(undefined)
  const earliestRenameFrom = useMemo(() => firstEditableMonday(new Date()), [])
  const effectiveRenameFrom = renameFromDate ?? earliestRenameFrom

  /** Codes are unique in the DB; checked here so a clash shows while typing. */
  const routeCodeClash = useMemo(() => {
    const code = routeFormData.name.trim()
    if (!code) return undefined
    return (routesPlannedData?.data as RoutePlanned[] | undefined)?.find(
      (rp) => rp.code === code && rp.id !== selectedRouteForEdit?.id,
    )
  }, [routeFormData.name, routesPlannedData, selectedRouteForEdit])

  // Dates come from routesPlannedData: filteredRoutes overwrites date with selectedDate.
  const openEditRouteDialog = useCallback(
    (route: PlannedRoute) => {
      const routePlanned = routesPlannedData?.data?.find((rp: RoutePlanned) => rp.id === route.id)
      const effectiveFromDate = routePlanned
        ? startOfDay(new Date(routePlanned.effective_from))
        : (() => {
            const originalRoute = routes.find((r) => r.id === route.id)
            return originalRoute ? startOfDay(originalRoute.date) : startOfDay(route.date)
          })()
      const effectiveToDate = routePlanned?.effective_to
        ? new Date(routePlanned.effective_to)
        : (routes.find((r) => r.id === route.id)?.recurringEndDate ?? route.recurringEndDate)
      setSelectedRouteForEdit(route)
      setIsCreatingNewRoute(false)
      setRenameFromDate(undefined)
      setRouteFormData({
        name: route.code,
        category: (route.category as RouteCategory) || "Other",
        operator: route.operator || "HIVED",
        vehicleType:
          route.vehicleType || vehicleTypeOptions[0] || "HGV-Curtainsider-350mi",
        // Crew size is a plan property; the timeline route doesn't carry it.
        crewSize: routePlanned?.crew_size ?? 1,
        routeKind: resolveRouteKind(routePlanned?.route_kind),
        status: route.status,
        isRecurring: route.isRecurring,
        recurringDays: route.recurringDays || [],
        effectiveFromDate,
        effectiveToDate: effectiveToDate ?? undefined,
        excludedDates: route.excludedDates ?? [],
        startTime: route.plannedStartTime ? format(route.plannedStartTime, "HH:mm") : "",
        endTime: route.plannedEndTime ? format(route.plannedEndTime, "HH:mm") : "",
      })
      setEditRouteDialogOpen(true)
      setInstanceEditMode(null)
    },
    [routes, routesPlannedData, vehicleTypeOptions, setInstanceEditMode],
  )

  const openCreateRouteDialog = useCallback(() => {
    setSelectedRouteForEdit(null)
    setIsCreatingNewRoute(true)
    setRouteFormData({
      name: "",
      category: "Other",
      operator: "HIVED",
      vehicleType: vehicleTypeOptions[0] || "HGV-Curtainsider-350mi",
      crewSize: 1,
      routeKind: "operational",
      status: "draft",
      isRecurring: false,
      recurringDays: [],
      effectiveFromDate: selectedDate,
      effectiveToDate: undefined,
      excludedDates: [],
      startTime: "",
      endTime: "",
    })
    setEditRouteDialogOpen(true)
  }, [selectedDate, vehicleTypeOptions])

  const handleSaveRoute = useCallback(async () => {
    const currentDayOfWeek = dayOfWeekOf(selectedDate)
    const routeDays = routeFormData.isRecurring
      ? routeFormData.recurringDays.length > 0
        ? routeFormData.recurringDays
        : [currentDayOfWeek]
      : []

    const isTempId = (id: string | undefined) => !id || id.startsWith("temp-")

    let updatedRoute: EditableRoute
    // Captured before the new code lands, which resets isRenamingRoute.
    const wasRenaming = isRenamingRoute
    const renameBoundary = effectiveRenameFrom

    let nextRoutes: (EditableRoute)[]

    if (isCreatingNewRoute) {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      updatedRoute = {
        id: tempId,
        code: routeFormData.name,
        date: selectedDate,
        plannedStartTime: setMinutes(setHours(selectedDate, 8), 0),
        plannedEndTime: setMinutes(setHours(selectedDate, 16), 0),
        stops: [],
        totalStops: 0,
        depot: "",
        estimatedCost: 0,
        isRecurring: routeFormData.isRecurring,
        recurringDays: routeDays,
        recurringEndDate: routeFormData.isRecurring ? routeFormData.effectiveToDate : undefined,
        excludedDates: routeFormData.excludedDates ?? [],
        status: routeFormData.status,
        createdAt: new Date(),
        updatedAt: new Date(),
        category: routeFormData.category,
        operator: routeFormData.operator,
        vehicleType: routeFormData.vehicleType,
        // Also set locally, or the side list shows one driver's wage until the refetch.
        crewSize: routeFormData.crewSize,
        route_kind: routeFormData.routeKind,
        effectiveFromDate: routeFormData.isRecurring
          ? routeFormData.effectiveFromDate
          : selectedDate,
      } as EditableRoute
      nextRoutes = [...routes, updatedRoute]
    } else if (selectedRouteForEdit) {
      const effectiveFrom = routeFormData.isRecurring
        ? (routeFormData.effectiveFromDate ?? selectedRouteForEdit.date)
        : selectedRouteForEdit.date
      updatedRoute = {
        ...selectedRouteForEdit,
        code: routeFormData.name,
        status: routeFormData.status,
        isRecurring: routeFormData.isRecurring,
        recurringDays: routeDays,
        recurringEndDate: routeFormData.isRecurring ? routeFormData.effectiveToDate : undefined,
        excludedDates: routeFormData.excludedDates ?? [],
        date: effectiveFrom,
        updatedAt: new Date(),
        category: routeFormData.category,
        operator: routeFormData.operator,
        vehicleType: routeFormData.vehicleType,
        // Also set locally, or the side list shows one driver's wage until the refetch.
        crewSize: routeFormData.crewSize,
        route_kind: routeFormData.routeKind,
        effectiveFromDate: routeFormData.isRecurring
          ? routeFormData.effectiveFromDate
          : selectedRouteForEdit.date,
      } as EditableRoute
      // Re-anchor stops to the new effective date so overnight day offsets stay right.
      const oldRouteDate = startOfDay(selectedRouteForEdit.date)
      const newRouteDate = startOfDay(effectiveFrom)
      if (oldRouteDate.getTime() !== newRouteDate.getTime() && updatedRoute.stops.length > 0) {
        updatedRoute = {
          ...updatedRoute,
          stops: shiftStopsToDate(updatedRoute.stops, oldRouteDate, newRouteDate),
        }
        if (updatedRoute.stops.length > 0) {
          updatedRoute = {
            ...updatedRoute,
            plannedStartTime: updatedRoute.stops[0].plannedArrival,
            plannedEndTime: updatedRoute.stops[updatedRoute.stops.length - 1].plannedDeparture,
          }
        }
      }
      nextRoutes = routes.map((route) =>
        route.id === selectedRouteForEdit.id ? updatedRoute : route,
      )
    } else {
      return
    }

    if (!hasUnsavedChanges && originalRoutes.length === 0) {
      setOriginalRoutes(
        routes.map(cloneRouteDates),
      )
    }
    setRoutes(nextRoutes)
    setHasUnsavedChanges(true)

    setIsSaving(true)
    try {
      const stopsPayload = plannedStopsPayload(updatedRoute)
      const effectiveFromForPayload =
        (updatedRoute as { effectiveFromDate?: Date }).effectiveFromDate ?? updatedRoute.date
      const routePayload = {
        code: updatedRoute.code,
        name: updatedRoute.code,
        category: updatedRoute.category || "Other",
        days: recurringDaysForSave(updatedRoute),
        default_start_time: formatTimeForDb(updatedRoute.plannedStartTime),
        default_end_time: formatTimeForDb(updatedRoute.plannedEndTime),
        depot: updatedRoute.depot,
        operator: updatedRoute.operator || "HIVED",
        vehicle_type: updatedRoute.vehicleType,
        // Set only here; the bulk save omits crew_size, which the edge function reads as "leave it".
        crew_size: routeFormData.crewSize,
        // Likewise, so dragging a stop can't turn a stand-by route operational.
        route_kind: routeFormData.routeKind,
        estimated_cost: updatedRoute.estimatedCost,
        status: updatedRoute.status,
        effective_from: format(effectiveFromForPayload, "yyyy-MM-dd"),
        effective_to: updatedRoute.recurringEndDate
          ? format(updatedRoute.recurringEndDate, "yyyy-MM-dd")
          : undefined,
        excluded_dates: futureExcludedDates(updatedRoute.excludedDates),
      }

      let newOriginals: (EditableRoute)[]
      let routesToCheck = nextRoutes

      if (isTempId(updatedRoute.id)) {
        const result = await savePlannedRoute.mutateAsync({
          route: routePayload,
          stops: stopsPayload,
        })
        const savedId = result.routeId
        const finalRoutes = nextRoutes.map((r) =>
          r.id === updatedRoute.id ? { ...r, id: savedId } : r,
        )
        setRoutes(finalRoutes)
        routesToCheck = finalRoutes
        newOriginals = originalRoutes.filter((r) => r.id !== updatedRoute.id)
        newOriginals.push({ ...updatedRoute, id: savedId } as EditableRoute)
      } else {
        const saveResult = await savePlannedRoute.mutateAsync({
          routeId: updatedRoute.id,
          route: routePayload,
          stops: stopsPayload,
          cascade: true,
          fromDate: format(selectedDate, "yyyy-MM-dd"),
          // Without it the server renames the template only, not generated instances.
          ...(wasRenaming && {
            renameFromDate: format(renameBoundary, "yyyy-MM-dd"),
          }),
        })
        if (saveResult?.cascadeError) {
          toast.error("Route saved, but future instances could not be updated", {
            description: saveResult.cascadeError,
          })
        }
        if (saveResult?.rename) {
          const { instancesRenamed, instancesSkipped } = saveResult.rename
          toast.success(`Renamed to ${routePayload.code}`, {
            description:
              `${instancesRenamed} daily route${instancesRenamed === 1 ? "" : "s"} renamed from ` +
              `${formatDayMonth(renameBoundary)}.` +
              (instancesSkipped > 0
                ? ` ${instancesSkipped} kept the old code — already sent to Track-POD or already run.`
                : ""),
          })
        }
        newOriginals = originalRoutes.map((r) =>
          r.id === updatedRoute.id ? { ...updatedRoute } : r,
        )
        if (!newOriginals.find((r) => r.id === updatedRoute.id)) {
          newOriginals.push({ ...updatedRoute } as EditableRoute)
        }
      }

      const hasOtherChanges = routesToCheck.some(
        (r) => !routeEqualsOriginal(r, newOriginals.find((o) => o.id === r.id) ?? r),
      )
      setOriginalRoutes(hasOtherChanges ? newOriginals : [])
      setHasUnsavedChanges(hasOtherChanges)

      await refetchInstances()
      await refetchRoutes()
      toast.success(isCreatingNewRoute ? "Route created" : "Changes saved", {
        description: isCreatingNewRoute
          ? "Route and excluded dates saved."
          : "Route and excluded dates saved.",
      })
      setEditRouteDialogOpen(false)
      setSelectedRouteForEdit(null)
      setIsCreatingNewRoute(false)
    } catch (error) {
      console.error("Error saving route:", error)
      toast.error("Error saving route", {
        description: error instanceof Error ? error.message : "Failed to save. Please try again.",
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    isCreatingNewRoute,
    selectedRouteForEdit,
    routeFormData,
    selectedDate,
    routes,
    originalRoutes,
    hasUnsavedChanges,
    savePlannedRoute,
    refetchRoutes,
    refetchInstances,
    isRenamingRoute,
    effectiveRenameFrom,
    setRoutes,
    setOriginalRoutes,
    setHasUnsavedChanges,
    setIsSaving,
  ])

  return {
    editRouteDialogOpen,
    setEditRouteDialogOpen,
    selectedRouteForEdit,
    routeFormData,
    setRouteFormData,
    isCreatingNewRoute,
    isRenamingRoute,
    effectiveRenameFrom,
    setRenameFromDate,
    routeCodeClash,
    vehicleTypeOptions,
    openEditRouteDialog,
    openCreateRouteDialog,
    handleSaveRoute,
  }
}
